/** @jsx jsx */
import { createContext, useContext } from 'react';
import { css, jsx } from '@emotion/react';
import invariant from 'tiny-invariant';
import { GRID_COLUMNS } from './config';
import { GridContainerContext } from './grid-container';
import { gapMediaQueries, inlinePaddingMediaQueries } from './grid-media-querys';
var baseStyles = css({
  display: 'grid',
  boxSizing: 'border-box',
  width: '100%',
  gridTemplateColumns: "repeat(".concat(GRID_COLUMNS, ", 1fr)"),
  marginInline: 'auto'
});
var gridMaxWidthMap = {
  wide: css({
    maxWidth: '70.5rem'
  }),
  narrow: css({
    maxWidth: '46.5rem'
  })
};
var GridContext = /*#__PURE__*/createContext(false);

/**
 * __Grid__
 *
 * A grid is a responsive layout component designed to manage the content of a page.
 *
 * - [Code](https://atlassian.design/components/grid)
 *
 * @example
 * ```jsx
 * import Grid, { GridItem } from '@atlaskit/grid';
 *
 * const App = () => (
 *   <Grid>
 *     <GridItem></GridItem>
 *   </Grid>
 * );
 * ```
 */
export var Grid = function Grid(_ref) {
  var testId = _ref.testId,
    children = _ref.children,
    maxWidth = _ref.maxWidth,
    hasInlinePadding = _ref.hasInlinePadding;
  var isNested = useContext(GridContext);
  var isWithinContainer = useContext(GridContainerContext);
  // while not within a GridContainer hasInlinePadding either undefined or true means setting the padding on by default
  var showInlinePadding = hasInlinePadding !== false;
  invariant(!isNested, '@atlaskit/grid: Nesting grids are not supported at this time, please only use a top-level grid or leverage GridContainer.');
  return jsx("div", {
    "data-testid": testId,
    css: [baseStyles,
    // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
    gapMediaQueries, !isWithinContainer && maxWidth && gridMaxWidthMap[maxWidth],
    // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
    !isWithinContainer && showInlinePadding && inlinePaddingMediaQueries]
  }, jsx(GridContext.Provider, {
    value: true
  }, children));
};